import { Localized } from "@/companyRegistry/companyRegistryApiClient";
import { Company, CompanyStatusDates, ResidentialAddress } from "./CompanyInfo";
import { RoleType } from "./enums/RoleType";
import { Language } from "@/i18n/languages";
import {
  AdminItem,
  CompanyItem,
  CompanyShareholderContact,
  FairValue,
  PhoneNumber,
  TaxAssessmentValue,
} from "@/common/common";
import { CustomRoleItem } from "@/common/customRole";
import { Module } from "@/common/product";

import { ShareType } from "@/common/share";
import {
  DocumentFont,
  LogoPlacementOption,
  SigningReminderSettings,
} from "@/overview/companySettings/companySettingsDomain";
import { OnboardingStatus } from "@/onboardingstatus/OnboardingStatusDomain";
import { AuditorListItem } from "@/dbot/common/commonDomain";
import { NamedOrganisationNumber } from "./Transactions";
import { User } from "./UserInfo";

export type GetCompanySettingsResponse = {
  company: Company;
  shareNominalValue: number;
  shareTypes: ShareType[];
  brregCompanyName: string | undefined;
  documentSettings: CompanyDocumentSettings | undefined;
  supportedFonts: DocumentFont[];
  taxAssessmentValues: TaxAssessmentValue[];
  fairValues: FairValue[];
  customRoles: CustomRoleItem[];
  modules: Module[];
  isShareholderPortalEnabled: boolean;
  shareholderContacts: CompanyShareholderContact[];
  signingReminderSettings?: SigningReminderSettings;
  overrideCostPriceDisplay?: boolean;
  hasIncompleteCostPriceHistory?: boolean;
  currentAccountant?: NamedOrganisationNumber;
  currentAuditor?: AuditorListItem;
  currentBusinessManager?: NamedOrganisationNumber;
  accountantUsers?: User[];
  companyStatusDates?: CompanyStatusDates;
  canEditCompanyStatus?: boolean;
  organisationTypeLabel?: Localized;
  registrationSubdivisionLabel?: Localized;
  hasRegistrationSubdivisions?: boolean;
  isCurrencyLocked?: boolean;
  canEditOrganisationNumber?: boolean;
  businessAddressEffectiveFrom?: string;
  postalAddressEffectiveFrom?: string;
};

export type CompanyDocumentSettings = {
  font: DocumentFont;
  logo: string;
  logoBase64?: string;
  logoPlacement?: LogoPlacementOption;
  defaultDocumentLanguages: Language[];
};

export type NewCompanyRoleRequest = {
  companyroleId?: number;
  firstName: string;
  lastName: string;
  middleName?: string;
  role?: string;
  accountRoleTypeId?: string;
  socialSecurityNumber: string;
  personReference?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  isResolving?: boolean;
  nationality?: string;
  dNumber?: string;
  validFrom?: string;
  validTo?: string;
  residentialAddress?: ResidentialAddress;
};

export type UpdateCompanyRoleRequest = {
  companyroleId?: number;
  role?: string;
  accountRoleTypeId?: string;
  email?: string;
  phoneNumber?: PhoneNumber;
  isResolving?: boolean;
  nationality?: string;
  dNumber?: string;
  validFrom?: string;
  validTo?: string;
  residentialAddress?: ResidentialAddress;
};

export type GetAdminsInCompaniesResponse = {
  admins: AdminItem[];
  availableCompanies: CompanyItem[];
};

export type GetPortfolioCustomRoleRequest = {
  customRoleItems: CustomRoleItem[];
  availableCompanies: CompanyItem[];
};

export type ListAdminAccessResponse = {
  admins: AdminItem[];
  portfolioCompanies: CompanyItem[];
};

export type GranteeAccess = {
  id: string;
  name: string;
  email: string;
  isRevoked: boolean;
};

export type UpdateAdminEmailRequest = {
  email: string;
  externalId: string;
  customRoleId?: string;
  portfolioId?: string;
};

export type RevokeAllRequest = {
  externalId: string;
  orgNos: string[];
};

export const settingsRoleTypes = [
  RoleType.CEO,
  RoleType.CHAIRPERSON,
  RoleType.MEMBER,
  RoleType.DEPUTY_CHAIRPERSON,
  RoleType.DEPUTY_MEMBER,
  RoleType.OBSERVER,
];

export type AffiliateItem = {
  id: string;
  key: string;
  active: boolean;
};

export type OnboardingStatusResponse = {
  articlesOfAssociation: OnboardingStatus;
  shareholderRegister: OnboardingStatus;
  settings: OnboardingStatus;
  payment: OnboardingStatus;
};

export type AltinnSystemUsersResponse = {
  hasStandardSystemUser: boolean;
  hasClientSystemUser: boolean;
};
